import React from 'react';
import { loadModulePromise } from 'src/components/YandexMap/mapLoader';
import { loadReactComponent } from 'src/components/YandexMap/loaders/componentLoaders';
import styled, { css, keyframes } from 'styled-components';
import type {
  Coordinates,
  ShortHotelInfo,
} from 'src/components/YandexMap/types';
import Pip from '@lt/components/icons-react/userflow/Pip';
import PointPrice from './components/PointPrice';
import { CENTER_OFFSET_DESKTOP, CENTER_OFFSET_MOBILE } from './constants';

const YMapMarker = loadReactComponent('YMapMarker', loadModulePromise);

type PointProps = {
  shortHotelInfo: ShortHotelInfo;
  isMobile: boolean;
  setPopupDataHandler: (popupData: ShortHotelInfo) => void;
  moveToPoint: (
    coordinates: Coordinates,
    zoomDiff: number,
    offset: { x: number; y: number },
  ) => void;
  geometry: {
    type: 'Point';
    coordinates: Coordinates;
  };
  source?: string;
  active?: boolean;
};

const Point = (props: PointProps) => {
  const {
    shortHotelInfo,
    setPopupDataHandler,
    moveToPoint,
    geometry,
    active = false,
    isMobile,
  } = props;

  const { visited } = shortHotelInfo;

  const openPopup = () => {
    setPopupDataHandler({
      ...shortHotelInfo,
      coordinates: geometry.coordinates,
    });
  };

  const onClick = () => {
    moveToPoint(geometry.coordinates, 0, {
      x: 0,
      y: isMobile ? CENTER_OFFSET_MOBILE : CENTER_OFFSET_DESKTOP,
    });
    openPopup();
  };

  return (
    <YMapMarker {...props} hideOutsideViewport>
      <MarkerPin $active={active}>
        <PriceWrapper $active={active} $visited={visited} onClick={onClick}>
          <PointPrice price={shortHotelInfo.price} />
        </PriceWrapper>
        <StyledPip $active={active} $visited={visited} onClick={onClick} />
      </MarkerPin>
    </YMapMarker>
  );
};

const fadeIn = keyframes`
  from {
      opacity: 0;
  }
  to {
      opacity: 1;
  }
`;

const MarkerPin = styled.div<{ $active: boolean }>`
  cursor: pointer;
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;

  animation: ${fadeIn} 0.25s ease;
`;

const PriceWrapper = styled.div<{ $visited: boolean; $active: boolean }>`
  background: #007aff;
  border: 1px solid #fff;
  border-radius: 12px;
  padding: 4px 8px 5px 8px;
  transition: 0.1s all ease-in-out;

  font-size: 12px;

  ${(props) =>
    !props.$active &&
    css`
      &:hover {
        border-radius: 16px;
        font-size: 16px;
      }
    `}

  ${(props) =>
    props.$visited &&
    css`
      background: #4b137b;
    `}

    ${(props) =>
    props.$active &&
    css`
      background: black;
      border-radius: 16px;
      font-size: 16px;
    `}
`;

const StyledPip = styled(Pip)<{ $active: boolean; $visited: boolean }>`
  height: 11.05px;
  width: 33px;

  margin-top: -1px;

  ${(props) =>
    props.$active &&
    css`
      > path {
        fill: black;
      }
    `}

  ${(props) =>
    props.$visited &&
    css`
      > path {
        fill: #4b137b;
      }
    `}
`;

export default Point;
